import { Close, EventOutlined, LockOutlined } from "@mui/icons-material"
import { Box, CircularProgress, FormControl, IconButton, InputBase, InputLabel, MenuItem, Select, SelectChangeEvent, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material"
import { useState } from "react"
import { PlaylistItems } from "../../Model/PlaylistModel"
import { Template } from "../../Model/TemplateModel"
import { PriceTable } from "../../Model/PriceTableModel"
import config from "../../Util/config"

const MIN_SEC = config.FEATURE.MIN_SECONDS
const MAX_SEC = config.FEATURE.MAX_SECONDS

type Props = {
  item: PlaylistItems
  templates: Template[]
  add: (n: number, item: PlaylistItems) => void
  sub: (n: number, item: PlaylistItems) => void
  removeItem: (item: PlaylistItems) => void
  setTemplate: (item: PlaylistItems, template: Template) => void
  isFixed: boolean
  isFixedTemplate: boolean
  onSchedule: (item: PlaylistItems) => void
}

const CardTable = (props: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [draft, setDraft] = useState<string | null>(null)

  const table = props.item.data as PriceTable | undefined
  const order = String(props.item.order).padStart(2, '0')

  if (!table) return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 1, p: 1.5,
      position: 'relative' }}>
      <IconButton size="small" onClick={() => props.removeItem(props.item)}
        sx={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, opacity: 0.4,
          '&:hover': { opacity: 1, color: 'error.main' } }}>
        <Close sx={{ fontSize: 13 }} />
      </IconButton>
      <CircularProgress size={20} thickness={4} />
      <Typography variant="caption" color="text.secondary" textAlign="center">
        Carregando tabela...
      </Typography>
    </Box>
  )
  const durationDisplay = draft !== null ? draft : String(props.item.seconds)

  function handleSelectTemplate(e: SelectChangeEvent<any>) {
    const template = props.templates.find(t => t.id === e.target.value)
    if (template) props.setTemplate(props.item, template)
  }

  function applyDraft() {
    if (draft === null) return
    const val = parseInt(draft, 10)
    if (!isNaN(val) && val > 0) {
      const clamped = Math.min(Math.max(val, MIN_SEC), MAX_SEC)
      const diff = clamped - props.item.seconds
      if (diff > 0) props.add(diff, props.item)
      else if (diff < 0) props.sub(-diff, props.item)
    }
    setDraft(null)
  }

  const ColorPreview = () => (
    props.item.template ? (
      <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
        <Box sx={{ flex: 1, bgcolor: props.item.template.color1 }} />
        <Box sx={{ flex: 1, bgcolor: props.item.template.color2 }} />
        <Box sx={{ flex: 1, bgcolor: props.item.template.textColor }} />
        <Box sx={{ flex: 1, bgcolor: props.item.template.priceColor }} />
      </Box>
    ) : (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
        <Typography variant="caption" color="text.disabled">Sem template</Typography>
      </Box>
    )
  )

  if (isMobile) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 1, pb: 0.5, gap: 1 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', letterSpacing: '0.05em' }}>
            {order}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>Tabela</Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={props.item.schedule ? 'Agendamento ativo' : 'Agendar'}>
            <IconButton size="small" onClick={() => props.onSchedule(props.item)}
              sx={{ width: 44, height: 44, color: props.item.schedule ? 'primary.main' : 'text.disabled' }}>
              <EventOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => props.removeItem(props.item)}
            sx={{ width: 44, height: 44, '&:hover': { color: 'error.main' } }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ mx: 1.5, borderRadius: 1.5, overflow: 'hidden', height: 64 }}>
          <ColorPreview />
        </Box>

        <Box sx={{ px: 1.5, pt: 1, pb: 1.5 }}>
          <Typography variant="body2" fontWeight={500} noWrap sx={{ mb: 1.5 }}>
            {table.name}
          </Typography>

          <FormControl size="small" fullWidth disabled={props.isFixedTemplate} sx={{ mb: 1.5 }}>
            <InputLabel>Template</InputLabel>
            <Select label="Template" value={props.item.template ? props.item.template.id : ''} onChange={handleSelectTemplate}>
              {props.templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>

          {props.isFixed ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LockOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">Tempo fixo</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>Duração:</Typography>
              <IconButton size="small" onClick={() => props.sub(1, props.item)} sx={{ width: 44, height: 44 }}>
                <Typography sx={{ fontSize: 20, lineHeight: 1, fontWeight: 300 }}>−</Typography>
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
                <InputBase
                  value={durationDisplay}
                  onChange={e => setDraft(e.target.value.replace(/\D/g, ''))}
                  onFocus={e => { setDraft(String(props.item.seconds)); e.target.select() }}
                  onBlur={applyDraft}
                  onKeyDown={e => { if (e.key === 'Enter') applyDraft() }}
                  inputProps={{ style: { textAlign: 'center', padding: 0, fontWeight: 700, fontSize: '1rem', width: `${Math.max(2, durationDisplay.length)}ch` } }}
                  sx={{ color: 'primary.main' }}
                />
                <Typography variant="body2" color="text.secondary">s</Typography>
              </Box>
              <IconButton size="small" onClick={() => props.add(1, props.item)} sx={{ width: 44, height: 44 }}>
                <Typography sx={{ fontSize: 20, lineHeight: 1, fontWeight: 300 }}>+</Typography>
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  // Desktop
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.25, height: 32, gap: 0.5, flexShrink: 0 }}>
        <Typography sx={{
          fontSize: '0.7rem', fontWeight: 700, color: 'text.disabled',
          letterSpacing: '0.05em', flexShrink: 0,
        }}>
          {props.item.order}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', flexShrink: 0 }}>·</Typography>
        <Typography sx={{
          fontSize: '0.72rem', fontWeight: 600, color: 'text.primary',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
        }}>
          {table.name}
        </Typography>
        <Tooltip title={props.item.schedule ? 'Agendamento ativo' : 'Agendar'}>
          <IconButton size="small" onClick={() => props.onSchedule(props.item)} sx={{
            width: 22, height: 22, flexShrink: 0,
            color: props.item.schedule ? 'primary.main' : 'transparent',
            opacity: props.item.schedule ? 1 : 0.25,
            '&:hover': { opacity: 1, color: 'primary.main' },
            transition: 'opacity 0.15s, color 0.15s',
          }}>
            <EventOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => props.removeItem(props.item)} sx={{
          width: 22, height: 22, flexShrink: 0, opacity: 0.25,
          '&:hover': { opacity: 1, color: 'error.main' },
          transition: 'opacity 0.15s, color 0.15s',
        }}>
          <Close sx={{ fontSize: 13 }} />
        </IconButton>
      </Box>

      {/* Preview de cores */}
      <Box sx={{ flex: 1, overflow: 'hidden', mx: 1, borderRadius: 1.5, minHeight: 0 }}>
        <ColorPreview />
      </Box>

      {/* Footer */}
      <Box sx={{ px: 1.25, pt: 0.5, pb: 1, flexShrink: 0 }}>
        <FormControl fullWidth disabled={props.isFixedTemplate} sx={{ mb: 0.75 }}>
          <Select
            value={props.item.template ? props.item.template.id : ''}
            onChange={handleSelectTemplate}
            displayEmpty
            renderValue={val => {
              if (!val) return <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>Template...</Typography>
              const t = props.templates.find(t => t.id === val)
              return <Typography sx={{ fontSize: '0.68rem', fontWeight: 500 }}>{t?.name}</Typography>
            }}
            sx={{ '& .MuiSelect-select': { py: '4px', px: '8px' } }}
          >
            {props.templates.map(t => <MenuItem key={t.id} value={t.id} sx={{ fontSize: '0.72rem' }}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {props.isFixed ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LockOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'text.secondary' }}>
                {props.item.seconds}s
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <IconButton onClick={() => props.sub(1, props.item)}
                disabled={props.item.seconds <= MIN_SEC}
                sx={{
                  width: 36, height: 36, color: 'text.secondary',
                  border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                  '&.Mui-disabled': { opacity: 0.3 },
                }}>
                <Typography sx={{ fontSize: 22, lineHeight: 1, fontWeight: 300, userSelect: 'none' }}>−</Typography>
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, minWidth: 48, justifyContent: 'center' }}>
                <InputBase
                  value={durationDisplay}
                  onChange={e => setDraft(e.target.value.replace(/\D/g, ''))}
                  onFocus={e => { setDraft(String(props.item.seconds)); e.target.select() }}
                  onBlur={applyDraft}
                  onKeyDown={e => { if (e.key === 'Enter') applyDraft() }}
                  inputProps={{
                    style: {
                      textAlign: 'center', padding: 0,
                      fontWeight: 700, fontSize: '1.25rem',
                      width: `${Math.max(2, durationDisplay.length)}ch`,
                      color: 'inherit',
                    }
                  }}
                  sx={{ color: 'primary.main', cursor: 'text' }}
                />
                <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', lineHeight: 1, fontWeight: 500 }}>s</Typography>
              </Box>
              <IconButton onClick={() => props.add(1, props.item)}
                disabled={props.item.seconds >= MAX_SEC}
                sx={{
                  width: 36, height: 36, color: 'text.secondary',
                  border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                  '&.Mui-disabled': { opacity: 0.3 },
                }}>
                <Typography sx={{ fontSize: 22, lineHeight: 1, fontWeight: 300, userSelect: 'none' }}>+</Typography>
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default CardTable
